import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
  dateFieldPlaceholder,
  dateHintLabel,
  dayFromSurveyStorageIso,
  surveyDatePickerFormatToken,
  surveyDateSelectableBounds,
  surveyStorageIsoFromDay,
} from '../../../lib/date-answer'
import type { SurveyQuestion } from '../../../lib/survey-question.types'
import { SurveyCalendarPickerIcon } from '../SurveyCalendarPickerIcon'
import {
  surveyDateHintLabelSx,
  surveyDatePickerCalendarSlotProps,
  surveyDatePickerFieldSx,
  surveyDatePickerRootSx,
} from '../../../styles/surveyExecutionStyles'

interface DateQuestionProps {
  readonly question: SurveyQuestion
  readonly value: string | null
  readonly onChange: (isoDate: string) => void
}

export function DateQuestion({ question, value, onChange }: DateQuestionProps) {
  const fieldId = `survey-q-${question.id}-date-input`
  const hint = dateHintLabel(question)
  const inFieldPlaceholder = dateFieldPlaceholder(question)
  const { minDate, maxDate } = surveyDateSelectableBounds(question)
  const pickerValue = dayFromSurveyStorageIso(value ?? '')

  return (
    <Box sx={surveyDatePickerRootSx} data-testid={`survey-q-${question.id}-date`}>
      {hint ? (
        <Typography
          component="label"
          htmlFor={fieldId}
          sx={surveyDateHintLabelSx}
          data-testid={`survey-q-${question.id}-date-hint`}
        >
          {hint}
        </Typography>
      ) : null}
      <DatePicker
        format={surveyDatePickerFormatToken(question.dateInputFormat)}
        value={pickerValue}
        minDate={minDate}
        maxDate={maxDate}
        desktopModeMediaQuery="@media (min-width: 0px)"
        onChange={(next) => onChange(surveyStorageIsoFromDay(next))}
        slots={{ openPickerIcon: SurveyCalendarPickerIcon }}
        slotProps={{
          ...surveyDatePickerCalendarSlotProps,
          textField: {
            id: fieldId,
            fullWidth: true,
            hiddenLabel: true,
            placeholder: inFieldPlaceholder,
            inputProps: {
              'data-testid': fieldId,
              'aria-label': question.prompt,
            },
            sx: surveyDatePickerFieldSx,
          },
        }}
      />
    </Box>
  )
}
